import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';

import ClinicalTrialsApi from '../../../../api/data-sections/clinicalTrialsApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import StudyDetails from '../../../../components/DataSections/ClinicalTrials/Modal/Tabs/StudyDetails';
import StudyOutcomes from '../../../../components/DataSections/ClinicalTrials/Modal/Tabs/StudyOutcomes';
import StudySponsors from '../../../../components/DataSections/ClinicalTrials/Modal/Tabs/StudySponsors';
import { ClinicalTrialsModel } from '../../../../models/data-sections/clinical-trials/clinicalTrialsModel';
import { ClinicalTrialsOutcomesModel } from '../../../../models/data-sections/clinical-trials/outcomesModel';
import { ClinicalTrialsSponsorsModel } from '../../../../models/data-sections/clinical-trials/sponsorsModel';
import { ClinicalTrialsStudiesModel } from '../../../../models/data-sections/clinical-trials/studiesModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface ClinicalTrialsModalProps {
  searchExecutionId: string;
  selected_study: ClinicalTrialsModel;
  onClose: () => void;
}

const ClinicalTrialsModal: React.FC<ClinicalTrialsModalProps> = ({
  searchExecutionId,
  selected_study,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [outcomeData, setOutcomeData] = useState<ClinicalTrialsOutcomesModel[] | null>(null);
  const [sponsorData, setSponsorData] = useState<ClinicalTrialsSponsorsModel[] | null>(null);
  const [studyData, setStudyData] = useState<ClinicalTrialsStudiesModel[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchApigetOutcomesByStudyId(searchExecutionId, selected_study.nct_id);
        await fetchApigetSponsorsByStudyId(searchExecutionId, selected_study.nct_id);
        await fetchApigetStudyById(searchExecutionId, selected_study.nct_id, new Date(selected_study.__ci_system_search_date_key ?? 0));
      } catch (error) {
        console.error('Error during sequential fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_study]);

  const fetchApigetOutcomesByStudyId = async (search_execution_id: string, nct_id: string): Promise<void> => {
    try {
      const response = await ClinicalTrialsApi.getOutcomeDetails(search_execution_id, nct_id);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as ClinicalTrialsOutcomesModel[];
        setOutcomeData(data);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApigetSponsorsByStudyId = async (search_execution_id: string, nct_id: string): Promise<void> => {
    try {
      const response = await ClinicalTrialsApi.getSponsorDetails(search_execution_id, nct_id);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as ClinicalTrialsSponsorsModel[];
        setSponsorData(data);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApigetStudyById = async (search_execution_id: string, nct_id: string, __ci_system_search_date_key: Date): Promise<void> => {
    try {
      const response = await ClinicalTrialsApi.getStudyById(search_execution_id, nct_id, __ci_system_search_date_key);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as ClinicalTrialsStudiesModel[];
        setStudyData(data);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab label="Study Details" />
            <Tab label="Outcomes" />
            <Tab label="Sponsors" />
          </Tabs>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {activeTab === 0 && selected_study && (
              <StudyDetails selected_study={selected_study} study_details={studyData?.[0]} />
            )}
            {activeTab === 1 && outcomeData && <StudyOutcomes outcomes={outcomeData} />}
            {activeTab === 2 && sponsorData && <StudySponsors sponsors={sponsorData} />}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClinicalTrialsModal;
